import React, { Children, useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * Horizontal, swipeable carousel built on CSS scroll-snap (no extra deps).
 * Arrows page by one slide on desktop; dots show and jump to the current slide.
 * `slideWidth` accepts MUI responsive values, e.g. { xs: "86%", md: "48%" }.
 */
export default function Carousel({ children, slideWidth = { xs: "86%", sm: "70%", md: "48%" }, gap = 2, label = "Carousel" }) {
  const slides = Children.toArray(children);
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [edges, setEdges] = useState({ start: true, end: slides.length <= 1 });

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const items = [...el.children];
    const left = el.scrollLeft;
    let best = 0;
    items.forEach((item, i) => {
      if (Math.abs(item.offsetLeft - el.offsetLeft - left) < Math.abs(items[best].offsetLeft - el.offsetLeft - left)) best = i;
    });
    setActive(best);
    setEdges({ start: left <= 4, end: left + el.clientWidth >= el.scrollWidth - 4 });
  }, []);

  useEffect(() => {
    update();
    const el = trackRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, slides.length]);

  const goTo = (i) => {
    const el = trackRef.current;
    const item = el?.children[i];
    if (!item) return;
    el.scrollTo({ left: item.offsetLeft - el.offsetLeft, behavior: "smooth" });
  };

  if (slides.length === 0) return null;

  const arrow = (dir) => (
    <IconButton
      aria-label={dir < 0 ? "Previous" : "Next"}
      onClick={() => goTo(Math.max(0, Math.min(slides.length - 1, active + dir)))}
      disabled={dir < 0 ? edges.start : edges.end}
      sx={{
        display: { xs: "none", md: "inline-flex" },
        position: "absolute",
        top: "50%",
        [dir < 0 ? "left" : "right"]: -18,
        transform: "translateY(-50%)",
        zIndex: 2,
        bgcolor: "#fff",
        border: "1px solid rgba(0,0,0,0.1)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
        "&:hover": { bgcolor: "#fff" },
        "&.Mui-disabled": { opacity: 0, pointerEvents: "none" },
      }}
    >
      {dir < 0 ? <ChevronLeftIcon /> : <ChevronRightIcon />}
    </IconButton>
  );

  return (
    <Box role="region" aria-roledescription="carousel" aria-label={label} sx={{ position: "relative" }}>
      {arrow(-1)}
      <Box
        ref={trackRef}
        sx={{
          display: "flex",
          gap,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          pb: 0.5,
        }}
      >
        {slides.map((slide, i) => (
          <Box
            key={i}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
            sx={{ flex: "0 0 auto", width: slides.length === 1 ? "100%" : slideWidth, scrollSnapAlign: "start" }}
          >
            {slide}
          </Box>
        ))}
      </Box>
      {arrow(1)}
      {slides.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 1.5 }}>
          {slides.map((_, i) => (
            <Box
              key={i}
              component="button"
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === active}
              onClick={() => goTo(i)}
              sx={{
                width: i === active ? 22 : 8,
                height: 8,
                p: 0,
                border: 0,
                borderRadius: 4,
                cursor: "pointer",
                bgcolor: i === active ? "#111" : "rgba(0,0,0,0.18)",
                transition: "width 0.2s ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
